import React from 'react';
import { GetMeApiSchemaContract } from 'schemas/GetMeApiSchema';
import Allowance, { amountTypes } from 'utils/Allowance';
import { SalaryPreviewTableData } from './SalaryPreviewTable';
import { Link } from '@razorpay/blade/components';
import { SalaryStructureComponent, taxableValues } from './constants';

export const getDefaultSalaryStructureComponents = ({
  organizationDefaultSalaryStructure,
  annualCtc = 0,
}: {
  organizationDefaultSalaryStructure:
    | NonNullable<GetMeApiSchemaContract['currentOrganization']>['defaultSalaryStructureConfig']
    | undefined;
  annualCtc?: Number;
}): SalaryPreviewTableData[] => {
  return [
    {
      id: 'basic',
      name: 'Basic Salary',
      annualAmount:
        (Number(annualCtc) * (organizationDefaultSalaryStructure?.basicPercentage ?? 0)) / 100,
      monthlyAmount:
        (Number(annualCtc) * (organizationDefaultSalaryStructure?.basicPercentage ?? 0)) / 1200,
    },
    {
      id: 'hra',
      name: 'HRA',
      annualAmount:
        (Number(annualCtc) * (organizationDefaultSalaryStructure?.hraPercentage ?? 0)) / 100,
      monthlyAmount:
        (Number(annualCtc) * (organizationDefaultSalaryStructure?.hraPercentage ?? 0)) / 1200,
    },
    {
      id: 'sa',
      name: 'Special Allowance',
      annualAmount:
        (Number(annualCtc) *
          (organizationDefaultSalaryStructure?.specialAllowancePercentage ?? 0)) /
        100,
      monthlyAmount:
        (Number(annualCtc) *
          (organizationDefaultSalaryStructure?.specialAllowancePercentage ?? 0)) /
        1200,
    },
    {
      id: 'lta',
      name: 'Leave & Travel Allowance',
      annualAmount:
        (Number(annualCtc) * (organizationDefaultSalaryStructure?.ltaPercentage ?? 0)) / 100,
      monthlyAmount:
        (Number(annualCtc) * (organizationDefaultSalaryStructure?.ltaPercentage ?? 0)) / 1200,
    },
  ];
};

export const getCustomAllowances = ({
  customAllowances,
  annualCtc = 0,
}: {
  customAllowances?: Allowance[];
  annualCtc?: Number;
}) => {
  return (
    customAllowances?.map((allowance) => {
      const annualAmount =
        allowance.amountType === amountTypes.ALLOWANCE_AMOUNT_TYPE_PERCENTAGE
          ? (Number(annualCtc) * Number(allowance.value || 0)) / 100
          : annualCtc ? allowance.value ?? 0 : 0;
      return {
        id: allowance.name,
        name: allowance.name,
        annualAmount,
        monthlyAmount: annualAmount / 12,
      };
    }) || []
  );
};

export const getOrgSalaryStructureComponents = ({
  salaryStructureConfig,
  annualCtc = 0,
  customAllowances,
  isNpsEnabled,
}: {
  salaryStructureConfig:
    | NonNullable<GetMeApiSchemaContract['currentOrganization']>['salaryStructureConfig']
    | undefined;
  annualCtc?: Number;
  customAllowances?: Allowance[];
  isNpsEnabled: boolean;
}): SalaryPreviewTableData[] => {
  const standardSalaryComponents = [
    {
      id: 'basic',
      name: 'Basic Salary',
      annualAmount: (Number(annualCtc) * (salaryStructureConfig?.percBasic ?? 0)) / 100,
      monthlyAmount: (Number(annualCtc) * (salaryStructureConfig?.percBasic ?? 0)) / 1200,
    },
    {
      id: 'da',
      name: 'Dearness Allowance',
      annualAmount: (Number(annualCtc) * (salaryStructureConfig?.percDa ?? 0)) / 100,
      monthlyAmount: (Number(annualCtc) * (salaryStructureConfig?.percDa ?? 0)) / 1200,
    },
    {
      id: 'hra',
      name: 'HRA',
      annualAmount: (Number(annualCtc) * (salaryStructureConfig?.percHra ?? 0)) / 100,
      monthlyAmount: (Number(annualCtc) * (salaryStructureConfig?.percHra ?? 0)) / 1200,
    },
    {
      id: 'sa',
      name: 'Special Allowance',
      annualAmount: (Number(annualCtc) * (salaryStructureConfig?.percSpecialAllowance ?? 0)) / 100,
      monthlyAmount:
        (Number(annualCtc) * (salaryStructureConfig?.percSpecialAllowance ?? 0)) / 1200,
    },
    {
      id: 'lta',
      name: 'Leave & Travel Allowance',
      annualAmount: (Number(annualCtc) * (salaryStructureConfig?.percLta ?? 0)) / 100,
      monthlyAmount: (Number(annualCtc) * (salaryStructureConfig?.percLta ?? 0)) / 1200,
    },
  ];

  if (isNpsEnabled) {
    standardSalaryComponents.push({
      id: 'nps',
      name: 'NPS Contribution',
      annualAmount: (Number(annualCtc) * (salaryStructureConfig?.percNpsLimit ?? 0)) / 100,
      monthlyAmount: (Number(annualCtc) * (salaryStructureConfig?.percNpsLimit ?? 0)) / 1200,
    });
  }

  return [...standardSalaryComponents, ...getCustomAllowances({ customAllowances, annualCtc })];
};

export const getTaxableLabel = (
  component: SalaryStructureComponent,
  referenceLinks: NonNullable<GetMeApiSchemaContract['appConfig']>['referenceLinks'],
) => {
  if (component.id === 'hra') {
    return (
      <Link href={referenceLinks?.linkHra || ''} target="_blank">
        Partially
      </Link>
    );
  }
  const taxableLabel = taxableValues.find((t) => t.value === component.taxable)?.label || 'No';
  return taxableLabel;
};
